import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Alert,
  Grid,
  Switch,
  Tooltip,
  Segmented
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import CustomResponseEditor, { validateCustomResponse } from '../../../shared/components/cdn/CustomResponseEditor';

const { Option } = Select;
const { useBreakpoint } = Grid;

const PAGE_TYPES = [
  { value: 'waf_block', label: 'WAF 拦截页', description: '触发 WAF 规则时显示' },
  { value: 'rate_limit', label: '限速页 (429)', description: '请求频率超限时显示' },
  { value: 'challenge', label: '人机验证页', description: '需要人机验证时显示' },
  { value: 'maintenance', label: '维护页', description: '网站维护时显示' },
  { value: 'http_error', label: 'HTTP 错误页', description: '自定义 HTTP 错误状态码页面' },
  { value: 'waiting_room', label: 'Waiting room page', description: 'Shown while visitors queue because the origin is busy' }
];

// 本域名自定义响应 settings.custom_responses(覆盖账号级页面)
const CR_KINDS = [
  { key: 'waf_block', label: 'WAF block', status: 403, presets: [403, 406, 451] },
  { key: 'rate_limit', label: 'Rate limit', status: 429, presets: [429, 503] },
  { key: 'origin_shield', label: 'Origin busy', status: 503, presets: [503, 429] },
  { key: 'waiting_room', label: 'Waiting room', status: 503, presets: [503, 200] }
];

const HTTP_STATUS_CODES = [400, 401, 403, 404, 408, 429, 500, 502, 503, 504];

const CONTENT_TYPES = [
  { value: 'text/html', label: 'HTML' },
  { value: 'text/plain', label: '纯文本' },
  { value: 'application/json', label: 'JSON' }
];

export default function ErrorPages() {
  const { t } = useTranslation();
  const { domainData, reloadDomain } = useOutletContext();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [form] = Form.useForm();

  const selectedPageType = Form.useWatch('page_type', form);

  // 维护模式(本域名):settings.error_page.maintenance → 边缘整域返回「维护页」(503)
  const errSettings = (() => {
    let s = domainData?.settings || {};
    if (typeof s === 'string') { try { s = JSON.parse(s); } catch (e) { s = {}; } }
    return s?.error_page || {};
  })();
  const [maintSaving, setMaintSaving] = useState(false);
  const toggleMaintenance = (on) => {
    if (!on) return saveMaintenance(false);
    Modal.confirm({
      title: t('Turn on maintenance mode?'),
      content: t('All requests to this domain will return the maintenance page'),
      okText: t('OK'),
      cancelText: t('Cancel'),
      onOk: () => saveMaintenance(true)
    });
  };
  const saveMaintenance = async (on) => {
    setMaintSaving(true);
    try {
      const res = await cdnService.updateDomain({
        id: domainData.id,
        settings: { error_page: { ...errSettings, maintenance: on } }
      });
      if (res.success) {
        message.success(on ? t('Maintenance mode is on') : t('Maintenance mode is off'));
        reloadDomain && reloadDomain();
      } else {
        message.error(res.error || t('Failed to save'));
      }
    } finally {
      setMaintSaving(false);
    }
  };

  // 本域名自定义响应
  const [crMap, setCrMap] = useState({});
  const [crKind, setCrKind] = useState('waf_block');
  const [crSaving, setCrSaving] = useState(false);
  useEffect(() => {
    let s = domainData?.settings || {};
    if (typeof s === 'string') { try { s = JSON.parse(s); } catch (e) { s = {}; } }
    const m = s?.custom_responses;
    setCrMap(m && typeof m === 'object' && !Array.isArray(m) ? m : {});
  }, [domainData]);
  const saveCustomResponses = async () => {
    const out = {};
    for (const k of CR_KINDS) {
      const cr = crMap[k.key];
      if (!cr || !String(cr.body || '').length) continue;
      const err = validateCustomResponse(cr, t);
      if (err) {
        message.error(`${t(k.label)}: ${err}`);
        return;
      }
      const item = { body: cr.body };
      if (cr.status) item.status = Number(cr.status);
      if (cr.content_type && String(cr.content_type).trim()) item.content_type = String(cr.content_type).trim();
      out[k.key] = item;
    }
    setCrSaving(true);
    try {
      const res = await cdnService.updateDomain({ id: domainData.id, settings: { custom_responses: out } });
      if (res.success) {
        message.success(t('Saved'));
        reloadDomain && reloadDomain();
      } else {
        message.error(res.error || t('Failed to save'));
      }
    } catch (e) {
      message.error(t('Failed to save'));
    } finally {
      setCrSaving(false);
    }
  };
  const crCur = CR_KINDS.find((k) => k.key === crKind) || CR_KINDS[0];

  useEffect(() => {
    loadPages();
  }, [domainData]);

  const loadPages = async () => {
    setLoading(true);
    try {
      const response = await cdnService.getErrorPages();
      if (response.success) {
        setPages(response.data || []);
      } else {
        message.error(response.error || t('Failed to load error pages'));
      }
    } catch (error) {
      console.error('Failed to load error pages:', error);
      message.error(t('Failed to load error pages'));
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    form.resetFields();
    setEditingPage(null);
    setModalVisible(true);
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    form.setFieldsValue({
      page_type: page.page_type,
      name: page.name,
      status_code: page.status_code,
      content_type: page.content_type || 'text/html',
      content: page.content
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      const response = await cdnService.deleteErrorPage(id);
      if (response.success) {
        message.success(t('Deleted successfully'));
        loadPages();
      } else {
        message.error(response.error || t('Deletion failed'));
      }
    } catch (error) {
      console.error('Failed to delete error page:', error);
      message.error(t('Deletion failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleLoadTemplate = async () => {
    const pageType = form.getFieldValue('page_type');
    if (!pageType) {
      message.warning(t('Please select a page type first'));
      return;
    }
    setTemplateLoading(true);
    try {
      const response = await cdnService.getErrorPageTemplates();
      if (response.success && response.data?.[pageType]) {
        form.setFieldValue('content', response.data[pageType].template);
        message.success(t('Template loaded'));
      } else {
        message.error(t('Failed to load template'));
      }
    } catch (error) {
      message.error(t('Failed to load template'));
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        page_type: values.page_type,
        name: values.name,
        content_type: values.content_type || 'text/html',
        content: values.content
      };
      if (values.page_type === 'http_error') {
        payload.status_code = values.status_code;
      }

      const response = await cdnService.saveErrorPage(payload);
      if (response.success) {
        message.success(editingPage ? t('Updated successfully') : t('Created successfully'));
        setModalVisible(false);
        loadPages();
      } else {
        message.error(response.error || t('Operation failed'));
      }
    } catch (error) {
      console.error('Failed to save error page:', error);
      message.error(t('Operation failed'));
    } finally {
      setLoading(false);
    }
  };

  const getPageTypeLabel = (type) => {
    const found = PAGE_TYPES.find(t => t.value === type);
    return found ? t(found.label) : type;
  };

  const columns = [
    {
      title: t('Type'),
      dataIndex: 'page_type',
      key: 'page_type',
      render: (type) => <Tag color="blue">{getPageTypeLabel(type)}</Tag>
    },
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: t('Status code'),
      dataIndex: 'status_code',
      key: 'status_code',
      render: (code) => code ? <Tag color="orange">{code}</Tag> : '-'
    },
    {
      title: t('Content type'),
      dataIndex: 'content_type',
      key: 'content_type',
      render: (ct) => ct || 'text/html'
    },
    {
      title: t('Updated at'),
      dataIndex: 'updated_at',
      key: 'updated_at'
    },
    {
      title: t('Actions'),
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >{t('Edit')}</Button>
          <Popconfirm
            title={t('Delete this error page?')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('OK')}
            cancelText={t('Cancel')}
            icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>{t('Delete')}</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const renderMobileCard = (page) => (
    <Card
      key={page.id}
      size="small"
      style={{ marginBottom: 12 }}
      extra={<Tag color="blue">{getPageTypeLabel(page.page_type)}</Tag>}
    >
      <div style={{ marginBottom: 6 }}>
        <strong>{page.name}</strong>
        {page.status_code && (
          <Tag color="orange" style={{ marginLeft: 8 }}>{page.status_code}</Tag>
        )}
      </div>
      <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>
        {page.content_type || 'text/html'} &nbsp;|&nbsp; {page.updated_at}
      </div>
      <Space>
        <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(page)}>{t('Edit')}</Button>
        <Popconfirm
          title={t('Delete this error page?')}
          onConfirm={() => handleDelete(page.id)}
          okText={t('OK')}
          cancelText={t('Cancel')}
        >
          <Button size="small" danger icon={<DeleteOutlined />}>{t('Delete')}</Button>
        </Popconfirm>
      </Space>
    </Card>
  );

  return (
    <div style={{ padding: isMobile ? '12px 0' : '24px 0' }}>
      <Card
        title={
          <Space>
            <ExclamationCircleOutlined />{t('Custom error pages')}</Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? t('Add') : t('Add error page')}
          </Button>
        }
        size={isMobile ? 'small' : 'default'}
      >
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Tooltip title={t('When on, all requests to this domain return the maintenance page (503); the default page is used if none is configured')}>
              <span>{t('Maintenance mode')}</span>
            </Tooltip>
            <Switch
              checked={!!errSettings.maintenance}
              loading={maintSaving}
              onChange={toggleMaintenance}
              disabled={!domainData?.id}
            />
          </Space>
        </div>
        <Alert
          message={t('Configuration instructions')}
          description={t('Custom error pages apply to all your domains. You can configure custom HTML pages for different error types, such as WAF blocks, rate limits, and maintenance.')}
          type="info"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />

        {isMobile ? (
          <div>
            {pages.map(page => renderMobileCard(page))}
            {pages.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>{t('No custom error pages yet. Default pages will be used.')}</div>
            )}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={pages}
            rowKey="id"
            loading={loading}
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: t('No custom error pages yet. Default pages will be used.') }}
          />
        )}
      </Card>

      {domainData?.id && (
        <Card
          title={(
            <Space>
              <span>{t('Custom responses for this domain')}</span>
              <Tooltip title={t('Overrides the account-level page for this domain')}>
                <ExclamationCircleOutlined style={{ color: '#999' }} />
              </Tooltip>
            </Space>
          )}
          size={isMobile ? 'small' : 'default'}
          style={{ marginTop: 16 }}
          extra={<Button type="primary" size={isMobile ? 'small' : 'middle'} loading={crSaving} onClick={saveCustomResponses}>{t('Save')}</Button>}
        >
          <div style={{ overflowX: 'auto', marginBottom: 16 }}>
            <Segmented
              value={crKind}
              onChange={setCrKind}
              options={CR_KINDS.map((k) => ({
                value: k.key,
                label: (
                  <span>
                    {t(k.label)}
                    {String(crMap[k.key]?.body || '').length > 0 && <Tag color="green" style={{ marginLeft: 6, marginRight: 0 }}>{t('Custom')}</Tag>}
                  </span>
                )
              }))}
            />
          </div>
          <Form layout="vertical" key={crKind}>
            <CustomResponseEditor
              value={crMap[crKind]}
              onChange={(v) => setCrMap((prev) => ({ ...prev, [crKind]: v }))}
              defaultStatus={crCur.status}
              statusPresets={crCur.presets}
              waitingRoom={crKind === 'waiting_room'}
            />
          </Form>
        </Card>
      )}

      <Modal
        title={editingPage ? t('Edit error page') : t('Add error page')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={loading}
        width={isMobile ? '100%' : 800}
        style={{ top: isMobile ? 10 : 40 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ content_type: 'text/html' }}
        >
          <Form.Item
            name="page_type"
            label={t('Page type')}
            rules={[{ required: true, message: t('Select a page type') }]}
          >
            <Select placeholder={t('Select an error page type')} disabled={!!editingPage}>
              {PAGE_TYPES.map(pageType => (
                <Option key={pageType.value} value={pageType.value}>
                  {t(pageType.label)}
                  <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>— {t(pageType.description)}</span>
                </Option>
              ))}
            </Select>
          </Form.Item>

          {selectedPageType === 'http_error' && (
            <Form.Item
              name="status_code"
              label={t('HTTP status code')}
              rules={[{ required: true, message: t('Select a status code') }]}
            >
              <Select placeholder={t('Select an HTTP status code')}>
                {HTTP_STATUS_CODES.map(code => (
                  <Option key={code} value={code}>{code}</Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="name"
            label={t('Name')}
            rules={[{ required: true, message: t('Enter a page name') }]}
          >
            <Input placeholder={t('E.g. custom 404 page')} />
          </Form.Item>

          <Form.Item name="content_type" label={t('Content type')}>
            <Select>
              {CONTENT_TYPES.map(ct => (
                <Option key={ct.value} value={ct.value}>{t(ct.label)}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="content"
            label={
              <Space>
                <span>{t('Page content')}</span>
                <Button
                  size="small"
                  loading={templateLoading}
                  onClick={handleLoadTemplate}
                >{t('Load template')}</Button>
              </Space>
            }
            rules={[{ required: true, message: t('Enter the page content') }]}
          >
            <Input.TextArea
              rows={12}
              placeholder={t('Enter HTML content here, or click “Load template” to use a preset template')}
              style={{ fontFamily: 'monospace', fontSize: 13 }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
